import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";
import { ApiException } from "../../../../services/api/ApiException";
import {
  deleteResourceCostById,
  getAllResourceCostByResourceId,
  updateResourceCost,
} from "../../../../services/api/resourceCost/resourceCostService";
import { IResourceCostType } from "../../../../@types/resource-cost.type";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { addOrUpdateResource } from "../../../../services/api/resource/resourceService";
import { IResourceType } from "../../../../@types/resource.type";
import { useConfirm } from "../../../../hooks/useConfirm";
import { ButtonDeleted } from "../../../../components/ButtonDeleted";
import { ButtonCreated } from "../../../../components/ButtonCreated";
import { ButtonSaved } from "../../../../components/ButtonSaved";
import { ButtonEdited } from "../../../../components/ButtonEdited";
import { ButtonCanceled } from "../../../../components/ButtonCancel";
import styles from "./styles.module.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

interface CostListProps {
  pageType: "create" | "update";
  resource: IResourceType | undefined;
  setExternalButtonsEnabled: (enabled: boolean) => void;
  setExternalSubmit: (costHistory: IResourceCostType) => void;
}

export const CostHistoryForm: React.FC<CostListProps> = ({
  pageType,
  resource,
  setExternalButtonsEnabled,
  setExternalSubmit,
}) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      effectiveDate: dayjs().tz("America/Sao_Paulo"),
      cost: 0,
    },
  });

  const [costHistory, setCostHistory] = useState<IResourceCostType[]>([]);
  const [showNewRecordForm, setShowNewRecordForm] = useState(false);
  const [editRecordId, setEditRecordId] = useState<string | null>(null);
  const [editableRecord, setEditableRecord] =
    useState<IResourceCostType | null>(null);
  const [originalRecord, setOriginalRecord] =
    useState<IResourceCostType | null>(null);
  const [showPaper, setShowPaper] = useState(true);

  useEffect(() => {
    const loadHistoryCost = async () => {
      await updateCostHistory();
    };
    loadHistoryCost();
  }, [resource]);

  useEffect(() => {
    if (pageType === "create") {
      setExternalSubmit(costHistory[0]);
    }
  }, [costHistory, pageType, setExternalSubmit]);

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o custo do colaborador?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const handleDelete = async (id: string) => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();
    try {
      if (isConfirm) {
        if (pageType === "update") {
          const response = await deleteResourceCostById(id);
          if (response.statusCode == 200) {
            toast.success("Custo do colaborador excluído com sucesso!");
            await updateCostHistory();
          } else {
            toast.success("Erro ao excluir custo do colaborador");
          }
        } else {
          setCostHistory([]);

          toast.success("Custo do colaborador excluído com sucesso!");
          setShowPaper(true);
          setExternalButtonsEnabled(true);
        }
      }
    } catch (error) {
      console.error("Erro ao excluir o custo:", error);
      toast.error("Erro ao excluir o custo.");
    } finally {
      if (costHistory.length <= 1) {
        setShowPaper(true);
      }
    }
  };

  const handleAddNew = async (data: IResourceCostType) => {
    if (data.effectiveDate) {
      if (pageType === "update" && resource?.id) {
        const newCost = {
          ...resource,
          resourceCost: {
            resourceId: resource?.id || "",
            cost: data.cost ? parseFloat(String(data.cost)) : 0,
            effectiveDate: dayjs(data.effectiveDate)
              .tz("America/Sao_Paulo")
              .toISOString(),
          },
        };

        try {
          const response = await addOrUpdateResource(newCost);

          if (response.statusCode === 200) {
            toast.success("Custo do colaborador salvo com sucesso!");
            await updateCostHistory();
            setExternalButtonsEnabled(true);
          } else {
            toast.error("Erro ao salvar o custo do Colaborador");
          }
        } catch (error: any) {
          toast.error("Erro ao salvar o custo do Colaborador");
          new ApiException(error.message || "Erro ao processar a requisição.");
        } finally {
          reset({ effectiveDate: dayjs().tz("America/Sao_Paulo") });
          setShowNewRecordForm(false);
        }
      } else {
        const resourceCost = {
          cost: data.cost ? parseFloat(String(data.cost)) : 0,
          effectiveDate: dayjs(data.effectiveDate)
            .tz("America/Sao_Paulo")
            .toISOString(),
        };

        const resourceCostList = [resourceCost];
        setCostHistory(resourceCostList);
        setExternalButtonsEnabled(true);
        reset({ effectiveDate: dayjs().tz("America/Sao_Paulo") });
        setShowNewRecordForm(false);
      }
    }
  };

  const updateCostHistory = async () => {
    if (resource && resource.id) {
      try {
        const updatedCostHistory = await getAllResourceCostByResourceId(
          resource.id
        );
        const sortedHistory = updatedCostHistory.sort(
          (a: IResourceCostType, b: IResourceCostType) =>
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
        );
        setCostHistory(sortedHistory);
      } catch (error) {
        console.error("Erro ao carregar o histórico de custos:", error);
      }
    }
  };

  const handleEdit = (record: IResourceCostType) => {
    setEditRecordId(record.id as string);
    setEditableRecord(record);
    setOriginalRecord(record);
    setExternalButtonsEnabled(false);
  };

  const handleCancelEdit = () => {
    setEditableRecord(originalRecord);
    setEditRecordId(null);
    setExternalButtonsEnabled(true);
  };

  const handleCreateItem = () => {
    setShowNewRecordForm(true);
    setShowPaper(false);
    setExternalButtonsEnabled(false);
  };

  const handleCancelItem = () => {
    if (costHistory.length === 0) {
      setShowPaper(true);
    }
    setShowNewRecordForm(false);
    setExternalButtonsEnabled(true);
  };

  const handleSaveEdit = async () => {
    if (editableRecord?.effectiveDate) {
      if (pageType === "update") {
        if (editableRecord) {
          const updatedRecord = {
            ...editableRecord,
            cost: editableRecord.cost,
            effectiveDate: editableRecord.effectiveDate,
          };

          try {
            const response = await updateResourceCost(updatedRecord);
            if (response.statusCode === 200) {
              toast.success("Custo do colaborador salvo com sucesso!");
              setEditRecordId(null);
              await updateCostHistory();
              setEditableRecord(null);
              setExternalButtonsEnabled(true);
            } else {
              toast.success("Erro ao salvar custo do colaborador");
            }
          } catch (error) {
            console.error("Erro ao salvar o custo:", error);
            toast.error("Erro ao salvar o custo.");
          }
        }
      } else {
        if (editableRecord) {
          setCostHistory([editableRecord]);
          toast.success("Custo do colaborador salvo com sucesso!");
          setEditRecordId(null);
          setEditableRecord(null);
          setExternalButtonsEnabled(true);
        }
      }
    }
  };
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 className={styles.title}>Custos</h3>
        {costHistory.length > 0 && pageType === "update" && (
          <ButtonCreated handleCreateItem={() => handleCreateItem()} />
        )}
      </div>
      {showNewRecordForm && (
        <div className={styles.containerInicial}>
          <div className={styles.fieldInicial}>
            <Controller
              name="effectiveDate"
              control={control}
              defaultValue={dayjs().tz("America/Sao_Paulo")}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Data de vigência"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newDate) =>
                    field.onChange(
                      newDate
                        ? newDate?.tz("America/Sao_Paulo").toISOString()
                        : null
                    )
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              )}
            />
          </div>
          {/* "FUTURO BREVE" */}
          <div className={styles.fieldInicial}>
            <Controller
              name="cost"
              control={control}
              defaultValue={0}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Custo Total por Hora"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  variant="outlined"
                  value={new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(field.value)}
                  onChange={(e) => {
                    const unformattedValue = e.target.value.replace(/\D/g, "");
                    field.onChange(
                      unformattedValue ? Number(unformattedValue) / 100 : 0
                    );
                  }}
                />
              )}
            />
          </div>
          <div className={styles.buttonsInicial}>
            <>
              <ButtonSaved handleSaveItem={handleSubmit(handleAddNew)} />
              <ButtonCanceled handleCancelItem={() => handleCancelItem()} />
            </>
          </div>
        </div>
      )}
      <div>
        {costHistory.length === 0 && showPaper ? (
          <Paper
            className={styles.projectPaper}
            style={{ textAlign: "center" }}
          >
            <div className={styles.projectPart1}>
              {"Adicione custos ao colaborador"}
            </div>
            <div className={styles.projectPart2}>
              {"É possível adicionar custos ao colaborador aqui"}
            </div>
            <div>
              <ButtonCreated handleCreateItem={handleCreateItem} />
            </div>
          </Paper>
        ) : (
          <div>
            {costHistory.map((record, index) => (
              <div
                key={record.id}
                className={index === 0 ? styles.container : styles.containerAlt}
              >
                <div className={index === 0 ? styles.field : styles.fieldAlt}>
                  <DatePicker
                    label="Data de vigência"
                    value={dayjs(record.effectiveDate)}
                    disabled={editRecordId !== record.id}
                    onChange={(newDate) => {
                      const updatedRecord = {
                        ...record,
                        effectiveDate: newDate
                          ? newDate.tz("America/Sao_Paulo").toISOString()
                          : null,
                      } as IResourceCostType;

                      setCostHistory((prevCostHistory) =>
                        prevCostHistory.map((rec) =>
                          rec.id === record.id ? updatedRecord : rec
                        )
                      );
                      setEditableRecord(updatedRecord);
                    }}
                    slotProps={{
                      textField: { fullWidth: true, variant: "outlined" },
                    }}
                  />
                </div>
                {/* "FUTURO BREVE" */}
                <div className={styles.field}>
                  <TextField
                    label="Custo Total por Hora"
                    value={new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(record.cost)}
                    onChange={(event) => {
                      const newValue = event.target.value.replace(/\D/g, "");
                      const costInCurrency = newValue
                        ? parseFloat(newValue) / 100
                        : 0;

                      const updatedRecord: IResourceCostType = {
                        ...record,
                        cost: costInCurrency,
                      };
                      setCostHistory((prevCostHistory: any) =>
                        prevCostHistory.map((rec: { id: string | undefined }) =>
                          rec.id === record.id ? updatedRecord : rec
                        )
                      );
                      setEditableRecord(updatedRecord);
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined"
                    disabled={editRecordId !== record.id}
                  />
                </div>
                <div className={styles.buttons}>
                  {editRecordId === record.id ? (
                    <>
                      <ButtonSaved handleSaveItem={() => handleSaveEdit()} />
                      <ButtonCanceled
                        handleCancelItem={() => handleCancelEdit()}
                      />
                    </>
                  ) : (
                    <>
                      <ButtonEdited
                        handleEditeItem={() => handleEdit(record)}
                      />
                      <ButtonDeleted
                        handleDeleteItem={() => handleDelete(record.id ?? "")}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {Confirm}
      </div>
    </div>
  );
};
